import React, { useMemo } from 'react';
import { Badge, Tabs } from 'antd';
import { useSelector } from 'react-redux';
import { BarChart3, Users, FileSearch, ScrollText, Wrench, LayoutDashboard } from 'lucide-react';
import { useIsMobileAdmin } from '../../hooks/useMediaQuery';
import { useAdminSocketContext } from '../../sockets/AdminSocketContext';
import AdminOverview from './AdminOverview';
import AdminWorkControl from './AdminWorkControl';
import AdminTechMonitor from './AdminTechMonitor';
import AdminUsers from './AdminUsers';
import AdminRequests from './AdminRequests';
import AdminAudit from './AdminAudit';
import { useAdminLiveSummary } from './useAdminLiveSummary';
import { useAdminUrlState } from './useAdminUrlState';
import './adminLayout.sass';

const ROLE_TABS = {
  admin: ['overview', 'work', 'tech', 'users', 'requests', 'audit'],
  manager: ['work'],
};

const AdminPanel = () => {
  const role = useSelector((state) => state.interface.role);
  const isAdmin = role === 'admin';
  const isMobile = useIsMobileAdmin();
  const api = useAdminSocketContext();

  const summary = useAdminLiveSummary({
    getOverview: api.getOverview,
    getPendingAged: api.getPendingAged,
    getDowntime: api.getDowntime,
    enabled: isAdmin,
  });

  const defaultTab = isAdmin ? 'overview' : 'work';
  const [storedTab, setActiveTab] = useAdminUrlState('tab', defaultTab);
  const allowedTabs = ROLE_TABS[role] || ROLE_TABS.manager;
  const activeTab = allowedTabs.includes(storedTab) ? storedTab : defaultTab;

  const alertDot = !isAdmin
    ? null
    : summary.overview?.error_period > 0
      ? '#ff4d4f'
      : summary.pendingAged > 0
        ? '#faad14'
        : null;

  const items = useMemo(() => {
    const label = (Icon, text, short, withBadge = false) => (
      <span className="admin-tab-label" title={text}>
        {withBadge && alertDot ? (
          <Badge dot color={alertDot} offset={[2, -2]}>
            <Icon size={16} className="admin-tab-label__icon" />
          </Badge>
        ) : (
          <Icon size={16} className="admin-tab-label__icon" />
        )}
        <span className="admin-tab-label__text">{isMobile ? short : text}</span>
      </span>
    );

    const all = [
      {
        key: 'overview',
        label: label(LayoutDashboard, 'Обзор', 'Обзор'),
        children: <AdminOverview summary={summary} onNavigate={setActiveTab} />,
      },
      {
        key: 'work',
        label: label(BarChart3, 'Контроль работы', 'Работа'),
        children: <AdminWorkControl />,
      },
      {
        key: 'tech',
        label: label(Wrench, 'Тех. мониторинг', 'Тех.', true),
        children: <AdminTechMonitor />,
      },
      {
        key: 'users',
        label: label(Users, 'Пользователи', 'Юзеры'),
        children: <AdminUsers />,
      },
      {
        key: 'requests',
        label: label(FileSearch, 'Запросы', 'Запросы'),
        children: <AdminRequests />,
      },
      {
        key: 'audit',
        label: label(ScrollText, 'Аудит', 'Аудит'),
        children: <AdminAudit />,
      },
    ];

    return all.filter((item) => allowedTabs.includes(item.key));
  }, [allowedTabs, isMobile, alertDot, summary, setActiveTab]);

  return (
    <div className="admin-panel">
      <header className="admin-panel__header">
        <div>
          <h2 className="admin-panel__title">Админ-панель</h2>
          <p className="admin-panel__subtitle">
            {isAdmin
              ? 'Сводка по сервису, контроль работы команды и технический мониторинг'
              : 'Контроль работы команды'}
          </p>
        </div>
      </header>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={items}
        size={isMobile ? 'middle' : 'large'}
        className="admin-panel__tabs"
        tabBarGutter={isMobile ? 8 : 16}
      />
    </div>
  );
};

export default AdminPanel;
